import { observer } from 'mobx-react';
import { Search, Calendar } from 'lucide-react';
import * as React from 'react';
import { L, T, TranslationsContext, translation } from '../../../../Translations';
import { aggregateAvailability, availabilityOf } from '../../LicenseAvailability';
import AvailabilityBar from '../../AvailabilityBar';
import { FormStore } from './Stores/FormStore';
import { License } from './Stores/Types';
import { UserGroupWithAssociations } from './Stores/ParticipantOptionsStore/Types';

interface LicenseSelectionSectionProps {
  formStore: FormStore;
  groups: UserGroupWithAssociations[];
  licenses: License[];
  canAddAsAuditor: boolean;
  handleLicenseToggle: (licenseId: number, checked: boolean) => void;
  toggleLicenseTypeExpanded: (typeName: string) => void;
}

function LicenseSelectionSection({
  formStore,
  groups,
  licenses,
  canAddAsAuditor,
  handleLicenseToggle,
  toggleLicenseTypeExpanded,
}: LicenseSelectionSectionProps) {
  const ts = React.useContext(TranslationsContext);

  const licensesByType = licenses.reduce((acc, license) => {
    const typeName = license.licenseTypeName;
    if (!acc.has(typeName)) {
      acc.set(typeName, []);
    }
    acc.get(typeName)!.push(license);
    return acc;
  }, new Map<string, typeof licenses>());

  const filteredTypes = Array.from(licensesByType.entries()).filter(([typeName]) =>
    typeName.toLowerCase().includes(formStore.licenseSearchQuery.toLowerCase()),
  );

  const sortedFilteredTypes = filteredTypes
    .map(
      ([typeName, typeLicenses]) =>
        [
          typeName,
          typeLicenses.sort((a, b) => {
            const nameA = a.licenseName.getOrElse(() => a.licenseTypeName);
            const nameB = b.licenseName.getOrElse(() => b.licenseTypeName);
            return nameA.localeCompare(nameB);
          }),
        ] as const,
    )
    .sort(([typeNameA], [typeNameB]) => typeNameA.localeCompare(typeNameB));

  return (
    <div className="bg-card border border-border rounded-lg p-8">
      <div className="flex items-start gap-4 mb-6">
        <div
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 text-lg"
          style={{ fontWeight: 'var(--font-weight-semibold)' }}
        >
          3
        </div>
        <div className="flex-1">
          <h2 className="text-xl mb-1" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            <T kind="Licenses" />
          </h2>
          <p className="text-muted-foreground">
            <T kind="Optionally allocate licenses. Licenses associated with groups are selected automatically." />
          </p>
        </div>
      </div>

      {formStore.groupSuggestedLicenseIds.size > 0 && (
        <div className="mb-6 bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex gap-3">
            <svg
              className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h4 style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                <T kind="Some licenses pre-selected from groups" />
              </h4>
              <p className="text-muted-foreground mt-1" style={{ fontSize: 'var(--text-sm)' }}>
                <T kind="The selected groups include associated collections and licenses. These licenses have been pre-selected below." />
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <input
          type="text"
          placeholder={translation('Search licenses...')(ts)}
          value={formStore.licenseSearchQuery}
          onChange={(e) => formStore.updateLicenseSearchQuery(e.target.value)}
          className="w-full px-4 py-2 pl-10 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-intercom-target="search-licenses-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <span className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
          <T
            kind="{{selectedCount}} of {{totalCount}} selected"
            selectedCount={formStore.selectedLicenseIds.length}
            totalCount={licenses.length}
          />
        </span>
        {formStore.selectedLicenseIds.length > 0 && (
          <button
            onClick={() => {
              formStore.updateSelectedLicenseIds([...formStore.groupSuggestedLicenseIds]);
              if (formStore.areLicensesAuditor) {
                formStore.updateAreLicensesAuditor(false);
              }
            }}
            className="text-primary hover:opacity-80 transition-opacity"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            data-intercom-target="clear-all-licenses-button"
          >
            <T kind="Clear all" />
          </button>
        )}
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
        {licenses.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="mb-2">
              <T kind="No licenses available" />
            </h3>
            <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="There are no active licenses. Contact your ExecOnline account team to add more licenses." />
            </p>
          </div>
        ) : sortedFilteredTypes.length === 0 ? (
          <div className="p-8 text-center border border-dashed border-border rounded-lg bg-muted/10">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T
                kind='No licenses found matching "{{query}}"'
                query={formStore.licenseSearchQuery}
              />
            </p>
          </div>
        ) : (
          sortedFilteredTypes.map(([typeName, licenses]) => {
            const isExpanded = formStore.expandedLicenseTypes.has(typeName);
            const nonSuggestedLicenses = licenses.filter(
              (l) => !formStore.groupSuggestedLicenseIds.has(l.id),
            );
            const allGroupSuggested = nonSuggestedLicenses.length === 0;
            const allSelected =
              nonSuggestedLicenses.length > 0 &&
              nonSuggestedLicenses.every((l) => formStore.selectedLicenseIds.includes(l.id));
            const someSelected = nonSuggestedLicenses.some((l) =>
              formStore.selectedLicenseIds.includes(l.id),
            );
            return (
              <div key={typeName} className="border border-border rounded-lg overflow-hidden">
                <div className="p-4 bg-card">
                  <div className="flex items-start gap-3 mb-3">
                    {allGroupSuggested ? (
                      <div className="mt-0.5 relative w-5 h-5 flex-shrink-0 rounded border-2 border-border bg-muted flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-foreground/60"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    ) : (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const licenseIds = licenses.map((l) => l.id);
                            formStore.updateSelectedLicenseIds([
                              ...new Set([...formStore.selectedLicenseIds, ...licenseIds]),
                            ]);
                          } else {
                            const nonSuggestedIds = new Set(
                              licenses
                                .filter((l) => !formStore.groupSuggestedLicenseIds.has(l.id))
                                .map((l) => l.id),
                            );
                            formStore.updateSelectedLicenseIds(
                              formStore.selectedLicenseIds.filter((id) => !nonSuggestedIds.has(id)),
                            );
                          }
                        }}
                        className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                          {typeName}
                        </span>
                        <span className="px-2 py-0.5 bg-muted/50 text-muted-foreground rounded text-xs flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <rect
                              x="3"
                              y="4"
                              width="18"
                              height="18"
                              rx="2"
                              ry="2"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <line
                              x1="16"
                              y1="2"
                              x2="16"
                              y2="6"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <line
                              x1="8"
                              y1="2"
                              x2="8"
                              y2="6"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <line
                              x1="3"
                              y1="10"
                              x2="21"
                              y2="10"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <T kind="{{count}} license term" count={licenses.length} />
                        </span>
                      </div>
                      {(() => {
                        const availability = aggregateAvailability(
                          licenses.map((l) => l.quantityAvailable),
                        );

                        return (
                          <div className="mb-2">
                            <div
                              className="flex items-center mb-1"
                              style={{ fontSize: 'var(--text-sm)' }}
                            >
                              {availability.isOverActivated ? (
                                <span
                                  className="text-destructive"
                                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                                >
                                  <T
                                    kind="None available (Over-activated by {{count}})"
                                    count={availability.overActivated}
                                  />
                                </span>
                              ) : availability.available === 0 ? (
                                <span
                                  className="text-destructive"
                                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                                >
                                  <T kind="None available" />
                                </span>
                              ) : (
                                <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                                  <T
                                    kind="{{available}} of {{total}} available"
                                    available={availability.available}
                                    total={availability.capacity}
                                  />
                                </span>
                              )}
                            </div>
                            <AvailabilityBar availability={availability} size="md" />
                          </div>
                        );
                      })()}
                      <button
                        onClick={() => toggleLicenseTypeExpanded(typeName)}
                        className="flex items-center gap-1 text-primary hover:opacity-80"
                        style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                        }}
                        data-intercom-target="toggle-license-periods-button"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                        {isExpanded ? (
                          <T kind="Hide {{count}} license term" count={licenses.length} />
                        ) : (
                          <T kind="Show {{count}} license term" count={licenses.length} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="p-3 space-y-2 border-t border-border bg-muted/10">
                    {licenses.map((license) => {
                      const isSelected = formStore.selectedLicenseIds.includes(license.id);
                      const isGroupSuggested = formStore.groupSuggestedLicenseIds.has(license.id);
                      const licenseName = license.licenseName.getOrElse(() => '');
                      return (
                        <label
                          key={license.id}
                          className={`flex items-start gap-3 p-3 border rounded-md transition-colors ${
                            isGroupSuggested
                              ? 'bg-primary/10 border-primary/20 cursor-default'
                              : isSelected
                                ? 'bg-card border-accent cursor-pointer'
                                : 'bg-card border-border hover:border-border cursor-pointer'
                          }`}
                        >
                          {isGroupSuggested ? (
                            <div className="mt-0.5 relative w-4 h-4 flex-shrink-0 rounded border-2 border-border bg-muted flex items-center justify-center">
                              <svg
                                className="w-3 h-3 text-foreground/60"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={3}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          ) : (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleLicenseToggle(license.id, e.target.checked)}
                              className="w-5 h-5 mt-0.5 rounded cursor-pointer text-primary border-border focus:ring-primary"
                            />
                          )}
                          <div className="flex-1" style={{ fontSize: 'var(--text-sm)' }}>
                            <div className="mb-2">
                              <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                                {licenseName && `${licenseName} (`}
                                <L localizeable={license.startOn} format="short-month-day-year" />
                                {' - '}
                                <L localizeable={license.endOn} format="short-month-day-year" />
                                {licenseName && ')'}
                              </span>
                            </div>
                            {(() => {
                              const availability = availabilityOf(license.quantityAvailable);

                              return (
                                <div className="space-y-2">
                                  <div
                                    className="flex items-center justify-between text-muted-foreground"
                                    style={{ fontSize: 'var(--text-xs)' }}
                                  >
                                    <span>
                                      <T kind="Available" />
                                    </span>
                                    {availability.isOverActivated ? (
                                      <span
                                        className="text-destructive"
                                        style={{ fontWeight: 'var(--font-weight-semibold)' }}
                                      >
                                        <T
                                          kind="Over-activated by {{overActivated}} ({{overActivated}} of 0)"
                                          overActivated={availability.overActivated}
                                        />
                                      </span>
                                    ) : (
                                      <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                                        <T
                                          kind="{{usedCount}} of {{totalCount}}"
                                          usedCount={availability.available}
                                          totalCount={availability.capacity}
                                        />
                                      </span>
                                    )}
                                  </div>
                                  <AvailabilityBar availability={availability} size="sm" />
                                </div>
                              );
                            })()}
                            {isGroupSuggested && (
                              <div className="mt-2 pt-2 border-t border-border">
                                <p
                                  className="text-muted-foreground"
                                  style={{ fontSize: 'var(--text-xs)' }}
                                >
                                  <T
                                    kind="Allocated to: {{groups}}"
                                    groups={groups
                                      .filter(
                                        (g) =>
                                          formStore.selectedGroupIds.includes(g.id) &&
                                          g.associatedLicenseIds.includes(license.id),
                                      )
                                      .map((g) => g.name)
                                      .join(', ')}
                                  />
                                </p>
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {canAddAsAuditor && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex-1 pr-8">
            <div
              className="mb-1"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            >
              <T kind="Auditor License" />
            </div>
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="Set as an auditor. Auditors can preview courses and collections for review purposes and do not count towards your license activations." />
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={formStore.areLicensesAuditor}
            disabled={formStore.selectedLicenseIds.length === 0}
            onClick={() => formStore.updateAreLicensesAuditor(!formStore.areLicensesAuditor)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formStore.selectedLicenseIds.length === 0
                ? 'bg-muted opacity-50 cursor-not-allowed'
                : formStore.areLicensesAuditor
                  ? 'bg-primary'
                  : 'bg-muted'
            }`}
            data-intercom-target="auditor-license-toggle"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${formStore.areLicensesAuditor ? 'translate-x-6' : 'translate-x-1'}`}
            />
          </button>
        </div>
      )}
    </div>
  );
}

export default observer(LicenseSelectionSection);
